import { isTravelMode } from "./transport";
import type { Place, SavedPlace } from "./types";
import { isGooglePlace } from "./search";
import { namedPlace } from "./placeLabel";

const PREFIX = "derech.v1.";
export const TRAFFIC_CONSENT_VERSION = 2;
const memory = new Map<string, string>();
const failedWrites = new Set<string>();

function read(key: string): unknown {
  try {
    const raw =
      (failedWrites.has(key) ? memory.get(key) : globalThis.localStorage?.getItem(PREFIX + key)) ?? memory.get(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    try {
      return JSON.parse(memory.get(key) ?? "null");
    } catch {
      return undefined;
    }
  }
}

function write(key: string, value: unknown): void {
  const raw = JSON.stringify(value);
  memory.set(key, raw);
  try {
    globalThis.localStorage?.setItem(PREFIX + key, raw);
    failedWrites.delete(key);
  } catch {
    failedWrites.add(key);
    /* Private mode or full storage: keep this session usable. */
  }
}

function isPlace(value: unknown): value is Place {
  if (!value || typeof value !== "object") return false;
  const place = value as Partial<Place>;
  return (
    typeof place.id === "string" &&
    typeof place.name === "string" &&
    typeof place.address === "string" &&
    typeof place.lat === "number" &&
    Number.isFinite(place.lat) &&
    Math.abs(place.lat) <= 90 &&
    typeof place.lng === "number" &&
    Number.isFinite(place.lng) &&
    Math.abs(place.lng) <= 180
  );
}

export function getSavedPlaces(): SavedPlace[] {
  const stored = read("places");
  return Array.isArray(stored)
    ? stored.filter(
        (place): place is SavedPlace =>
          isPlace(place) &&
          !isGooglePlace(place) &&
          ["home", "work", "favorite"].includes((place as SavedPlace).kind),
      )
    : [];
}

/** Home and work are single slots; favorites are unique by place id. */
export function savePlace(place: SavedPlace): SavedPlace[] {
  if (isGooglePlace(place))
    throw new Error(
      "מקום מ־Google זמין לנסיעה הזו בלבד. לשמירה קבועה בחרו מקום מהחיפוש הרגיל.",
    );
  if (!isPlace(place) || !["home", "work", "favorite"].includes(place.kind))
    throw new Error("המקום אינו תקין");
  const places = getSavedPlaces().filter(
    (item) =>
      item.id !== place.id &&
      (place.kind === "favorite" || item.kind !== place.kind),
  );
  places.push({ ...place });
  write("places", places);
  return places;
}

export function removePlace(id: string): SavedPlace[] {
  const places = getSavedPlaces().filter((place) => place.id !== id);
  write("places", places);
  return places;
}

export function getRecents(): Place[] {
  const stored = read("recents");
  return Array.isArray(stored)
    ? stored
        .filter(isPlace)
        .filter((p) => !isGooglePlace(p))
        .map(namedPlace).filter((p): p is Place => p !== null)
        .slice(0, 8)
    : [];
}

export function addRecent(place: Place): Place[] {
  if (!isPlace(place)) throw new Error("המקום אינו תקין");
  if (isGooglePlace(place)) return getRecents();
  const named = namedPlace(place);
  if (!named) return getRecents();
  place = named;
  const places = [
    { ...place },
    ...getRecents().filter((item) => item.id !== place.id),
  ].slice(0, 8);
  write("recents", places);
  return places;
}

export function clearRecents(): void {
  write("recents", []);
}

export interface Settings {
  travelMode: import("./transport").TravelMode;
  showCycleways: boolean;
  showSpeed: boolean;
  speedWarning: boolean;
  speedSound: boolean;
  speedTolerance: number;
  voiceAlerts: boolean;
  reportAlerts: boolean;
  avoidDangers: boolean;
  shareTraffic: boolean;
  speechProvider: "f5" | "device";
}

export function getSettings(): Settings {
  const stored = read("settings") as (Partial<Settings> & { trafficConsentVersion?: number; voiceProviderPolicy?: number }) | undefined;
  return {
    travelMode: isTravelMode(stored?.travelMode) ? stored.travelMode : "car",
    showCycleways: stored?.showCycleways !== false,
    showSpeed: stored?.showSpeed !== false,
    speedWarning: stored?.speedWarning !== false,
    speedSound: stored?.speedSound === true,
    speedTolerance: [0, 5, 10].includes(stored?.speedTolerance as number) ? stored!.speedTolerance! : 0,
    voiceAlerts:
      typeof stored?.voiceAlerts === "boolean" ? stored.voiceAlerts : true,
    reportAlerts:
      typeof stored?.reportAlerts === "boolean" ? stored.reportAlerts : true,
    avoidDangers:
      typeof stored?.avoidDangers === "boolean" ? stored.avoidDangers : true,
    // A former opt-in to memory-only live traffic does not authorize retained trip durations.
    shareTraffic: stored?.shareTraffic === true && stored.trafficConsentVersion === TRAFFIC_CONSENT_VERSION,
    speechProvider: stored?.voiceProviderPolicy === 3 && stored.speechProvider === "f5" ? "f5" : "device",
  };
}

export function saveSettings(update: Partial<Settings>): Settings {
  const current = getSettings();
  const settings = {
    travelMode: isTravelMode(update.travelMode) ? update.travelMode : current.travelMode,
    showCycleways: typeof update.showCycleways === "boolean" ? update.showCycleways : current.showCycleways,
    showSpeed: typeof update.showSpeed === "boolean" ? update.showSpeed : current.showSpeed,
    speedWarning: typeof update.speedWarning === "boolean" ? update.speedWarning : current.speedWarning,
    speedSound: typeof update.speedSound === "boolean" ? update.speedSound : current.speedSound,
    speedTolerance: [0, 5, 10].includes(update.speedTolerance as number) ? update.speedTolerance! : current.speedTolerance,
    voiceAlerts:
      typeof update.voiceAlerts === "boolean"
        ? update.voiceAlerts
        : current.voiceAlerts,
    reportAlerts:
      typeof update.reportAlerts === "boolean"
        ? update.reportAlerts
        : current.reportAlerts,
    avoidDangers:
      typeof update.avoidDangers === "boolean"
        ? update.avoidDangers
        : current.avoidDangers,
    shareTraffic:
      typeof update.shareTraffic === "boolean"
        ? update.shareTraffic
        : current.shareTraffic,
    speechProvider:
      update.speechProvider === "f5" || update.speechProvider === "device"
        ? update.speechProvider
        : current.speechProvider,
  };
  write("settings", {
    ...settings,
    voiceProviderPolicy: 3,
    trafficConsentVersion: settings.shareTraffic ? TRAFFIC_CONSENT_VERSION : null,
  });
  return settings;
}

/** A pseudonymous device identifier, used for report ownership and one confirmation per device. */
export function getDeviceId(): string {
  const stored = read("device");
  if (
    typeof stored === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      stored,
    )
  )
    return stored.toLowerCase();
  let id: string;
  if (globalThis.crypto?.randomUUID) {
    id = globalThis.crypto.randomUUID();
  } else {
    // randomUUID requires a secure context; debug WebViews can still expose getRandomValues.
    const bytes = new Uint8Array(16);
    if (globalThis.crypto?.getRandomValues)
      globalThis.crypto.getRandomValues(bytes);
    else
      for (let i = 0; i < bytes.length; i++)
        bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
    id = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  write("device", id);
  return id;
}
